import React from "react";

interface FAQItem {
  question: string;
  answer: string;
  category?: string;
}

interface AIOptimizedStructuredDataProps {
  type: 'FAQ' | 'HowTo' | 'Article' | 'Service' | 'Organization' | 'WebPage' | 'Product';
  data: any;
  faqs?: FAQItem[];
  howToSteps?: Array<{
    name: string;
    text: string;
    image?: string;
  }>;
  articleData?: {
    headline: string;
    author: string;
    datePublished: string;
    dateModified: string;
    wordCount: number;
  };
}

export const AIOptimizedStructuredData = ({ 
  type, 
  data, 
  faqs, 
  howToSteps, 
  articleData 
}: AIOptimizedStructuredDataProps) => {
  const siteUrl = "https://www.londonsecondaryglazing.co.uk";
  
  const getAIOptimizedSchema = () => {
    switch (type) {
      case 'FAQ':
        return {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "name": data.title || "Frequently Asked Questions",
          "description": data.description || "Common questions about secondary glazing services",
          "url": data.url || `${siteUrl}/faq`,
          "inLanguage": "en-GB",
          "author": {
            "@type": "Organization",
            "@id": `${siteUrl}/#organization`,
            "name": "London Secondary Glazing"
          },
          "publisher": {
            "@type": "Organization",
            "@id": `${siteUrl}/#organization`
          },
          "datePublished": data.datePublished || new Date().toISOString(),
          "dateModified": data.dateModified || new Date().toISOString(),
          "mainEntity": faqs?.map((faq, index) => ({
            "@type": "Question",
            "@id": `${data.url || `${siteUrl}/faq`}#question-${index + 1}`,
            "name": faq.question,
            "text": faq.question,
            "answerCount": 1,
            "dateCreated": data.datePublished || new Date().toISOString(),
            "author": {
              "@type": "Organization",
              "@id": `${siteUrl}/#organization`
            },
            "acceptedAnswer": {
              "@type": "Answer",
              "@id": `${data.url || `${siteUrl}/faq`}#answer-${index + 1}`,
              "text": faq.answer,
              "dateCreated": data.datePublished || new Date().toISOString(),
              "url": `${data.url || `${siteUrl}/faq`}#answer-${index + 1}`,
              "author": {
                "@type": "Organization",
                "@id": `${siteUrl}/#organization`,
                "name": "London Secondary Glazing"
              }
            }
})) || []
        };

      case 'HowTo':
        return {
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": data.name,
          "description": data.description,
          "image": data.image ? `${siteUrl}${data.image}` : `${siteUrl}/og-image.png`,
          "estimatedCost": data.estimatedCost || {
            "@type": "MonetaryAmount",
            "currency": "GBP",
            "value": "500"
          },
          "supply": data.supplies || [
            {
              "@type": "HowToSupply",
              "name": "Secondary glazing units"
            },
            {
              "@type": "HowToSupply", 
              "name": "Professional installation tools"
            }
          ],
          "tool": data.tools || [
            {
              "@type": "HowToTool",
              "name": "Measuring equipment"
            }
          ],
          "totalTime": data.totalTime || "PT2H",
          "step": howToSteps?.map((step, index) => ({
            "@type": "HowToStep",
            "position": index + 1,
            "name": step.name,
            "text": step.text,
            "image": step.image ? `${siteUrl}${step.image}` : undefined,
            "url": `${data.url}#step-${index + 1}`
          })) || []
        };

      case 'Article':
        return {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": articleData?.headline || data.title,
          "description": data.description,
          "image": data.image ? `${siteUrl}${data.image}` : `${siteUrl}/og-image.png`,
          "author": {
            "@type": "Organization",
            "@id": `${siteUrl}/#organization`,
            "name": "London Secondary Glazing"
          },
          "publisher": {
            "@type": "Organization",
            "@id": `${siteUrl}/#organization`
          },
          "datePublished": articleData?.datePublished || new Date().toISOString(),
          "dateModified": articleData?.dateModified || new Date().toISOString(),
          "wordCount": articleData?.wordCount || 1500,
          "inLanguage": "en-GB",
          "url": data.url,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": data.url
          },
          "about": [
            {
              "@type": "Thing",
              "name": "Secondary Glazing"
            },
            {
              "@type": "Thing", 
              "name": "Home Improvement"
            },
            {
              "@type": "Thing",
              "name": "Energy Efficiency"
            }
          ],
          "keywords": data.keywords || "secondary glazing, energy efficiency, noise reduction, thermal insulation"
        };

      case 'Service':
        return {
          "@context": "https://schema.org",
          "@type": "Service",
          "name": data.name,
          "description": data.description,
          "provider": {
            "@type": "LocalBusiness",
            "@id": `${siteUrl}/#organization`
          },
          "serviceType": "Secondary Glazing Installation",
          "category": "Home Improvement",
          "areaServed": [
            {
              "@type": "City",
              "name": "London"
            },
            {
              "@type": "State",
              "name": "Greater London"
            }
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Secondary Glazing Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Acoustic Secondary Glazing",
                  "description": "Specialized glazing for noise reduction up to 45dB"
                },
                "priceCurrency": "GBP",
                "priceRange": "£££"
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service", 
                  "name": "Thermal Secondary Glazing",
                  "description": "Energy efficient glazing for heat retention"
                },
                "priceCurrency": "GBP",
                "priceRange": "£££"
              }
            ]
          }
        };

      default:
        return data;
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(getAIOptimizedSchema(), null, 2)
      }}
    />
  );
};